// src/lib/docWriteQueue.js
//
// Cola FIFO independiente por docKey. Garantiza que, para un mismo
// documento (workspace/día/perfil), exista un único escritor activo en
// cada momento: el autoguardado de AppCore y las transacciones de
// weekOfChange en dogDayManagerActions.js encolan aquí sus tareas usando
// exactamente la misma clave (ver src/lib/editLockKey.js).
//
// Requisitos cubiertos:
// - Cola FIFO por docKey (Map<docKey, Promise>).
// - Un rechazo de una tarea anterior NO bloquea la siguiente tarea de la
//   misma cola (se intercepta con .catch antes de encadenar la siguiente).
// - Al terminar la ÚLTIMA tarea encolada para un docKey, se borra la
//   entrada del Map (no queda memoria residual por documento).
// - El resultado/rechazo de `enqueueDocWrite` para el LLAMADOR refleja
//   fielmente el resultado de SU PROPIA tarea (no el de otras tareas
//   encoladas antes o después).

const queues = new Map(); // docKey -> Promise (tail actual de la cola)
const tailSentinels = new Map(); // docKey -> objeto sentinel único (tail actual)

/**
 * Encola `task` para ejecutarse después de todas las tareas previamente
 * encoladas con el mismo `docKey`, en orden FIFO.
 *
 * @param {string} docKey
 * @param {() => Promise<any>} task
 * @returns {Promise<any>} resuelve/rechaza con el resultado de `task`
 */
export function enqueueDocWrite(docKey, task) {
  const previous = queues.get(docKey) || Promise.resolve();
  const mySentinel = {};
  tailSentinels.set(docKey, mySentinel);

  // La cola avanza siempre: un rechazo anterior se intercepta ANTES de
  // encadenar la siguiente tarea, así nunca bloquea al resto de la cola.
  const chained = previous.catch(() => {}).then(() => task());

  queues.set(docKey, chained.catch(() => {}));

  chained.finally(() => {
    // Solo limpiar el Map si nadie encoló una tarea más nueva después de
    // la nuestra para este mismo docKey (es decir, si somos la última).
    if (tailSentinels.get(docKey) === mySentinel) {
      queues.delete(docKey);
      tailSentinels.delete(docKey);
    }
  });

  return chained;
}
